import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FiUploadCloud, FiPlus, FiTrash2, FiCheck, FiLoader, FiArrowLeft, FiRefreshCw, FiFileText, FiRepeat } from 'react-icons/fi';
import api, { ExpensesAPI, VendorsAPI, CompaniesAPI, RecurringExpensesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

/* ── colour tokens (shared w/ rest of app) ── */
const colors = {
  stone50: '#fafaf9', stone100: '#f5f5f4', stone200: '#e7e5e4', stone300: '#d6d3d1',
  stone400: '#a8a29e', stone500: '#78716c', stone600: '#57534e', stone700: '#44403c',
  stone800: '#292524', stone900: '#1c1917',
  accent500: '#06b6d4', accent600: '#0891b2',
  emerald50: '#ecfdf5', emerald600: '#059669',
  amber50: '#fffbeb', amber600: '#d97706',
  rose50: '#fff1f2', rose600: '#e11d48',
  blue50: '#eff6ff', blue600: '#2563eb',
};

const CATEGORIES = [
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'OFFICE_SUPPLIES', label: 'Office Supplies' },
  { value: 'EQUIPMENT', label: 'Equipment' },
  { value: 'SERVICES', label: 'Services' },
  { value: 'UTILITIES', label: 'Utilities' },
  { value: 'FOOD', label: 'Food' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'PAYROLL', label: 'Payroll' },
  { value: 'RENT_UTILITIES', label: 'Rent & Utilities' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'SOFTWARE_SUBSCRIPTIONS', label: 'Software & Subscriptions' },
  { value: 'FOOD_ENTERTAINMENT', label: 'Food & Entertainment' },
  { value: 'LOGISTICS', label: 'Logistics' },
  { value: 'PROFESSIONAL_FEES', label: 'Professional Fees' },
  { value: 'OTHER', label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: '', label: '- Select -' },
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CARD', label: 'Card' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'OTHER', label: 'Other' },
];

const emptyLine = () => ({
  name: '', description: '', hsnsac: '', quantity: 1, unit: 'NOS', price: 0,
  discountAmount: 0, discountPercent: 0, cgstRate: 0, sgstRate: 0, igstRate: 0, cessRate: 0,
});

function calcLine(line) {
  const qty = Number(line.quantity) || 0;
  const price = Number(line.price) || 0;
  const gross = qty * price;
  const discPct = (Number(line.discountPercent) / 100) * gross;
  const taxable = Math.max(0, gross - Number(line.discountAmount || 0) - discPct);
  const cgst = +(taxable * Number(line.cgstRate || 0) / 100).toFixed(2);
  const sgst = +(taxable * Number(line.sgstRate || 0) / 100).toFixed(2);
  const igst = +(taxable * Number(line.igstRate || 0) / 100).toFixed(2);
  const cess = +(taxable * Number(line.cessRate || 0) / 100).toFixed(2);
  return { taxableValue: +taxable.toFixed(2), cgstAmount: cgst, sgstAmount: sgst, igstAmount: igst, cessAmount: cess, lineTotal: +(taxable + cgst + sgst + igst + cess).toFixed(2) };
}

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const activeRows = (rows) => (Array.isArray(rows) ? rows : [])
  .filter((row) => String(row?.status || '').toLowerCase() !== 'voided');
const byLatest = (a, b) => (
  Number(b?.sequenceNo || 0) - Number(a?.sequenceNo || 0)
  || Number(b?.id || 0) - Number(a?.id || 0)
);
const latestVendorPayment = (expense) => activeRows(expense?.vendorPayments).sort(byLatest)[0] || null;
const latestReimbursement = (expense) => activeRows(expense?.reimbursements).sort(byLatest)[0] || null;

function round2(n) {
  return +Number(n || 0).toFixed(2);
}

function allocateDocTaxAmounts(itemsWithTaxable, doc) {
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const docCgst = round2(toNum(doc?.cgstAmount));
  const docSgst = round2(toNum(doc?.sgstAmount));
  const docIgst = round2(toNum(doc?.igstAmount));
  const docCess = round2(toNum(doc?.cessAmount));

  const pickTargets = (predicate) => {
    const idxs = itemsWithTaxable.map((it, i) => (predicate(it) ? i : -1)).filter((i) => i >= 0);
    return idxs.length ? idxs : itemsWithTaxable.map((_, i) => i);
  };

  const allocate = (total, predicate) => {
    const targets = pickTargets(predicate);
    const weights = targets.map((i) => Math.max(0, Number(itemsWithTaxable[i]?.taxableValue) || 0));
    const wsum = weights.reduce((a, b) => a + b, 0);
    const useEqual = wsum <= 0;
    let remaining = round2(total);
    const out = new Array(itemsWithTaxable.length).fill(0);

    for (let k = 0; k < targets.length; k++) {
      const idx = targets[k];
      if (k === targets.length - 1) {
        out[idx] = round2(out[idx] + remaining);
        break;
      }
      const portion = useEqual ? (total / targets.length) : (total * (weights[k] / wsum));
      const amt = round2(portion);
      out[idx] = round2(out[idx] + amt);
      remaining = round2(remaining - amt);
    }
    return out;
  };

  const cgstAlloc = allocate(docCgst, (it) => Number(it?.cgstRate) > 0);
  const sgstAlloc = allocate(docSgst, (it) => Number(it?.sgstRate) > 0);
  const igstAlloc = allocate(docIgst, (it) => Number(it?.igstRate) > 0);
  const cessAlloc = allocate(docCess, (it) => Number(it?.cessRate) > 0);

  return itemsWithTaxable.map((it, i) => {
    const taxableValue = round2(it.taxableValue);
    const cgstAmount = round2(cgstAlloc[i]);
    const sgstAmount = round2(sgstAlloc[i]);
    const igstAmount = round2(igstAlloc[i]);
    const cessAmount = round2(cessAlloc[i]);
    const lineTotal = round2(taxableValue + cgstAmount + sgstAmount + igstAmount + cessAmount);
    return { ...it, taxableValue, cgstAmount, sgstAmount, igstAmount, cessAmount, lineTotal };
  });
}

/* ── receipt image optimization (client-side) ── */
const RECEIPT_OPTIMIZE_THRESHOLD_BYTES = 1 * 1024 * 1024; // 1MB
const RECEIPT_MAX_DIMENSION = 1600; // OCR-friendly
const RECEIPT_ENCODE_QUALITY = 0.75;

function isPdfFile(file) {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return type.includes('pdf') || name.endsWith('.pdf');
}

function isImageFile(file) {
  const type = String(file?.type || '').toLowerCase();
  return type.startsWith('image/');
}

function getBaseName(filename) {
  const name = String(filename || 'receipt');
  const idx = name.lastIndexOf('.');
  if (idx <= 0) return name;
  return name.slice(0, idx);
}

function supportsWebpEncoding() {
  try {
    const c = document.createElement('canvas');
    // If unsupported, most browsers return "image/png" data URL.
    return c.toDataURL('image/webp').startsWith('data:image/webp');
  } catch (_) {
    return false;
  }
}

async function fileToDataUrl(file) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function dataUrlToImage(dataUrl) {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

async function canvasToBlob(canvas, mimeType, quality) {
  return await new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

/**
 * Prepare a receipt file for upload:
 * - PDFs: untouched
 * - Images > 1MB: downscale to max 1600px and encode to webp/jpeg @ ~0.75 quality
 * - Other: untouched
 *
 * Returns `{ file, optimized }`
 */
async function prepareReceiptFile(originalFile) {
  const file = originalFile;
  if (!file) return { file, optimized: false };
  if (isPdfFile(file)) return { file, optimized: false };
  if (!isImageFile(file)) return { file, optimized: false };
  if (file.size <= RECEIPT_OPTIMIZE_THRESHOLD_BYTES) return { file, optimized: false };

  try {
    const dataUrl = await fileToDataUrl(file);
    const img = await dataUrlToImage(dataUrl);

    let w = img.width || 0;
    let h = img.height || 0;
    if (!w || !h) return { file, optimized: false };

    const maxDim = Math.max(w, h);
    if (maxDim > RECEIPT_MAX_DIMENSION) {
      const scale = RECEIPT_MAX_DIMENSION / maxDim;
      w = Math.max(1, Math.round(w * scale));
      h = Math.max(1, Math.round(h * scale));
    }

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);

    const preferWebp = supportsWebpEncoding();
    const outType = preferWebp ? 'image/webp' : 'image/jpeg';
    const blob = await canvasToBlob(canvas, outType, RECEIPT_ENCODE_QUALITY);
    if (!blob) return { file, optimized: false };

    const base = getBaseName(file.name);
    const ext = outType === 'image/webp' ? 'webp' : 'jpg';
    const outName = `${base || 'receipt'}-optimized.${ext}`;
    const optimizedFile = new File([blob], outName, { type: outType, lastModified: Date.now() });

    // If optimization didn't help, keep original to avoid quality loss.
    if (optimizedFile.size >= file.size) return { file, optimized: false };

    return { file: optimizedFile, optimized: true };
  } catch (e) {
    // Fail open: upload original.
    return { file, optimized: false };
  }
}

function reconcileItemsToTargetTotal(rawItems, targetTotal) {
  const round2 = (n) => +Number(n || 0).toFixed(2);
  const EPS = 0.01; // match currency precision

  const target = round2(Number(targetTotal));
  if (!Number.isFinite(target) || target <= 0) return rawItems;

  const items = (rawItems || []).map((it) => ({ ...it }));
  const computed = items.map((it) => ({ ...it, ...calcLine(it) }));
  const sum = round2(computed.reduce((a, it) => a + (Number(it.lineTotal) || 0), 0));
  if (!sum || !Number.isFinite(sum)) return rawItems;

  const initialDelta = round2(target - sum);
  if (initialDelta === 0) return rawItems;

  // Safety: only auto-reconcile when mismatch looks like rounding / minor OCR drift.
  const maxAutoDelta = Math.max(5, round2(target * 0.01)); // ₹5 or 1% of total (whichever larger)
  if (Math.abs(initialDelta) > maxAutoDelta) return rawItems;

  // Allocate target total across lines proportionally to current line totals.
  const weights = computed.map((it) => Math.max(0.000001, Number(it.lineTotal) || 0));
  const wsum = weights.reduce((a, b) => a + b, 0);

  const scaled = items.map((it, idx) => {
    const qty = Math.max(0.000001, Number(it.quantity) || 1);
    const pct = Math.max(0, Math.min(99.99, Number(it.discountPercent) || 0)) / 100;
    const discAmt = Math.max(0, Number(it.discountAmount) || 0);
    const m = 1 + (
      (Number(it.cgstRate) || 0) +
      (Number(it.sgstRate) || 0) +
      (Number(it.igstRate) || 0) +
      (Number(it.cessRate) || 0)
    ) / 100;
    const lineTarget = target * (weights[idx] / wsum);

    // Solve gross from: lineTotal = (gross*(1-pct) - discAmt) * m
    // gross = (lineTotal/m + discAmt) / (1-pct)
    const denom = Math.max(0.000001, (1 - pct));
    const gross = Math.max(0, (lineTarget / Math.max(0.000001, m) + discAmt) / denom);
    const newPrice = round2(gross / qty);
    return { ...it, price: newPrice };
  });

  // Final correction: due to per-tax rounding, proportional scaling can still miss by a few paise.
  const pickCorrectionIndex = (rows) => {
    for (let i = rows.length - 1; i >= 0; i--) {
      const q = Number(rows[i]?.quantity);
      if (Number.isFinite(q) && q > 0) return i;
    }
    return rows.length - 1;
  };

  const applyTargetToSingleLine = (rows, idx, newLineTotalTarget) => {
    const it = rows[idx];
    if (!it) return rows;
    const qty = Math.max(0.000001, Number(it.quantity) || 1);
    const pct = Math.max(0, Math.min(99.99, Number(it.discountPercent) || 0)) / 100;
    const discAmt = Math.max(0, Number(it.discountAmount) || 0);
    const m = 1 + (
      (Number(it.cgstRate) || 0) +
      (Number(it.sgstRate) || 0) +
      (Number(it.igstRate) || 0) +
      (Number(it.cessRate) || 0)
    ) / 100;
    const denom = Math.max(0.000001, (1 - pct));
    const gross = Math.max(0, (Math.max(0, Number(newLineTotalTarget) || 0) / Math.max(0.000001, m) + discAmt) / denom);
    const newPrice = round2(gross / qty);
    const next = rows.slice();
    next[idx] = { ...it, price: newPrice };
    return next;
  };

  let corrected = scaled;
  for (let attempt = 0; attempt < 4; attempt++) {
    const c = corrected.map((it) => ({ ...it, ...calcLine(it) }));
    const s2 = round2(c.reduce((a, it) => a + (Number(it.lineTotal) || 0), 0));
    const delta = round2(target - s2);
    if (delta === 0) break;

    // If still off by more than a few paise after scaling, don't keep pushing prices around.
    if (Math.abs(delta) > Math.max(1, maxAutoDelta)) break;

    const idx = pickCorrectionIndex(corrected);
    const currentLineTotal = round2(c[idx]?.lineTotal || 0);
    const desiredLineTotal = Math.max(EPS, round2(currentLineTotal + delta));
    corrected = applyTargetToSingleLine(corrected, idx, desiredLineTotal);
  }

  return corrected;
}

/* ── styles ── */
const s = {
  page: { minHeight: '100vh', background: `linear-gradient(135deg, ${colors.stone50} 0%, ${colors.stone100} 100%)`, padding: '24px 32px' },
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', color: colors.stone500, display: 'flex', alignItems: 'center' },
  title: { fontSize: 22, fontWeight: 700, color: colors.stone800, margin: 0 },

  card: { background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,.06)', marginBottom: 20 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: colors.stone800, marginBottom: 16 },

  /* side-by-side layout */
  splitRow: { display: 'flex', gap: 20, alignItems: 'stretch' },
  splitLeft: { flex: 1, minWidth: 0 },
  splitRight: { flex: 1, minWidth: 0 },

  /* dropzone */
  dropzone: (drag) => ({
    position: 'relative',
    border: `2px dashed ${drag ? colors.accent500 : colors.stone300}`,
    borderRadius: 12, padding: 48, textAlign: 'center', cursor: 'pointer',
    background: drag ? colors.blue50 : colors.stone50,
    transition: 'all .2s', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    minHeight: 360,
  }),
  dropzoneText: { color: colors.stone500, fontSize: 14, marginTop: 8 },

  /* preview (replaces dropzone) */
  previewWrap: {
    position: 'relative', borderRadius: 12, overflow: 'hidden', border: `1px solid ${colors.stone200}`,
    background: colors.stone50, minHeight: 360, display: 'flex', flexDirection: 'column',
  },
  previewHeader: {
    padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: `1px solid ${colors.stone200}`, background: '#fff',
  },
  previewLabel: { fontSize: 12, fontWeight: 600, color: colors.stone600 },
  previewImg: { width: '100%', objectFit: 'contain', cursor: 'pointer', maxHeight: 420 },
  pdfEmbed: { width: '100%', flex: 1, minHeight: 520, border: 'none' },

  /* lightbox */
  lightbox: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
    background: 'rgba(0,0,0,.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  },
  lightboxImg: { maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,.4)' },

  /* OCR banner */
  ocrBanner: (c) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8,
    background: c === 'success' ? colors.emerald50 : c === 'loading' ? colors.amber50 : colors.rose50,
    color: c === 'success' ? colors.emerald600 : c === 'loading' ? colors.amber600 : colors.rose600,
    fontSize: 13, fontWeight: 500, marginTop: 12,
  }),
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(255,255,255,0.72)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    zIndex: 5
  },
  loadingText: { fontSize: 13, fontWeight: 700, color: colors.stone700 },

  cameraModal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200,
    padding: 20
  },
  cameraCard: {
    width: '100%',
    maxWidth: 720,
    background: '#fff',
    borderRadius: 16,
    border: `1px solid ${colors.stone200}`,
    overflow: 'hidden',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
  },
  cameraHeader: {
    padding: '12px 14px',
    borderBottom: `1px solid ${colors.stone200}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10
  },
  cameraTitle: { margin: 0, fontSize: 14, fontWeight: 800, color: colors.stone800 },
  cameraBody: { padding: 12, background: colors.stone50 },
  cameraVideoWrap: { width: '100%', borderRadius: 12, overflow: 'hidden', background: '#000', border: `1px solid ${colors.stone200}` },
  cameraVideo: { width: '100%', height: 420, objectFit: 'cover', display: 'block' },
  cameraFooter: { padding: 12, borderTop: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'flex-end', gap: 8 },

  /* form fields */
  row: { display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' },
  field: (flex = 1) => ({ flex, minWidth: 140 }),
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: colors.stone500, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.03em' },
  input: { width: '100%', padding: '8px 12px', border: `1px solid ${colors.stone200}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  select: { width: '100%', padding: '8px 12px', border: `1px solid ${colors.stone200}`, borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' },

  /* line-items table */
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 600, color: colors.stone500, borderBottom: `1px solid ${colors.stone200}`, textTransform: 'uppercase' },
  td: { padding: '6px 6px', verticalAlign: 'middle' },
  lineInput: { width: '100%', padding: '6px 8px', border: `1px solid ${colors.stone200}`, borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  lineNum: { width: 70, textAlign: 'right', padding: '6px 8px', border: `1px solid ${colors.stone200}`, borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  addLineBtn: { fontSize: 12, padding: '6px 10px', borderRadius: 8, height: 32, minHeight: 32, display: 'inline-flex', alignItems: 'center', gap: 6, textTransform: 'none' },
  removeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: colors.rose600, padding: 4 },

  /* totals */
  totalsRow: { display: 'flex', justifyContent: 'flex-end', gap: 24, padding: '12px 0', borderTop: `1px solid ${colors.stone200}`, marginTop: 12 },
  totalLabel: { fontSize: 13, color: colors.stone500 },
  totalValue: { fontSize: 15, fontWeight: 700, color: colors.stone800 },

  /* buttons - match Inventory.js */
  smallBtn: { fontSize: 12, padding: '4px 10px', borderRadius: 8, height: 25, minHeight: 25, display: 'inline-flex', alignItems: 'center', gap: 6, textTransform: 'none' },
  actionBtn: { textTransform: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 },
};

/* ────────────────────────────────────────── */
export default function ExpenseForm() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { company, user } = useAuth();
  const companyId = (() => {
    const safeParseJson = (v) => {
      try { return JSON.parse(v); } catch (_) { return null; }
    };
    const fromStoredCompany = (() => {
      const raw = localStorage.getItem('company');
      const parsed = raw ? safeParseJson(raw) : null;
      return parsed?.id ?? null;
    })();

    const candidates = [
      company?.id,
      user?.companyId,
      fromStoredCompany,
      localStorage.getItem('companyId')
    ];

    for (const raw of candidates) {
      const s = String(raw ?? '').trim();
      if (!s || s === 'null' || s === 'undefined') continue;
      const n = Number(s);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  })();
  const isEdit = !!params.id;
  const qs = new URLSearchParams(location.search || '');
  const isRecurringTemplateMode = !isEdit && qs.get('mode') === 'recurring';
  const recurringSeedId = !isEdit ? qs.get('recurringId') : '';
  const recurringSeedDate = !isEdit ? qs.get('occurrenceDate') : '';
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const liveVideoRef = useRef(null);
  const liveCanvasRef = useRef(null);
  const liveStreamRef = useRef(null);

  /* ── form state ── */
  const [vendorId, setVendorId] = useState(null);
  const [vendorName, setVendorName] = useState('');
  const [vendorGstin, setVendorGstin] = useState('');
  const [vendorPhone, setVendorPhone] = useState('');
  const [vendorEmail, setVendorEmail] = useState('');
  const [vendorWebsite, setVendorWebsite] = useState('');
  const [vendorAddress, setVendorAddress] = useState('');
  const [expNumber, setExpNumber] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [category, setCategory] = useState('OTHER');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([emptyLine()]);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [receiptS3Key, setReceiptS3Key] = useState('');
  const [ocrRaw, setOcrRaw] = useState(null);
  const [ocrConfidence, setOcrConfidence] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [exchangeRate, setExchangeRate] = useState('');
  const [convertedAmount, setConvertedAmount] = useState('');
  const [paymentProofUrl, setPaymentProofUrl] = useState('');
  const [statementProofReference, setStatementProofReference] = useState('');
  const [isReimbursement, setIsReimbursement] = useState(false);
  const [reimbursementApproverId, setReimbursementApproverId] = useState('');
  const [filedForType, setFiledForType] = useState('USER'); // USER | EMPLOYEE
  const [filedForId, setFiledForId] = useState('');
  const [buyerGstin, setBuyerGstin] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerAddress, setBuyerAddress] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [itcEligible, setItcEligible] = useState(false);
  const [recurringName, setRecurringName] = useState('');
  const [recurringBillingInterval, setRecurringBillingInterval] = useState('MONTH');
  const [recurringIntervalCount, setRecurringIntervalCount] = useState(1);
  const [recurringBillingAnchor, setRecurringBillingAnchor] = useState('ANNIVERSARY');
  const [recurringDayOfMonth, setRecurringDayOfMonth] = useState('');
  const [recurringStartDate, setRecurringStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [recurringAmount, setRecurringAmount] = useState('');
  const defaultUnitOptions = ['Number', 'Hour', 'Kilogram', 'Meter', 'Litre', 'Piece', 'Ton', 'NOS'];

  /* ── UI state ── */
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [ocrStatus, setOcrStatus] = useState(null);
  const [ocrMessage, setOcrMessage] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [localFileType, setLocalFileType] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [companyUsers, setCompanyUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [companyEmployees, setCompanyEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [usersLoadedOnce, setUsersLoadedOnce] = useState(false);
  const [employeesLoadedOnce, setEmployeesLoadedOnce] = useState(false);
  const [paidConfirmOpen, setPaidConfirmOpen] = useState(false);
  const [pendingSubmitStatus, setPendingSubmitStatus] = useState(null);
  const [companyGstin, setCompanyGstin] = useState('');

  const hasPreview = !!(localPreviewUrl || receiptUrl);

  const stopLiveCamera = useCallback(() => {
    if (liveStreamRef.current) {
      liveStreamRef.current.getTracks().forEach((t) => t.stop());
      liveStreamRef.current = null;
    }
    setCameraReady(false);
  }, []);

  useEffect(() => {
    return () => {
      stopLiveCamera();
    };
  }, [stopLiveCamera]);

  // Default "filed for" to current user (reimbursements)
  useEffect(() => {
    if (filedForId) return;
    if (user?.id) {
      setFiledForType('USER');
      setFiledForId(String(user.id));
    }
  }, [user?.id, filedForId]);

  const startLiveCamera = async () => {
    setCameraError('');
    setCameraOpen(true);
    setCameraReady(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      liveStreamRef.current = stream;
      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        liveVideoRef.current.onloadedmetadata = () => {
          liveVideoRef.current.play();
          setCameraReady(true);
        };
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setCameraError('Unable to access camera. Please allow permissions and try again.');
      setCameraOpen(false);
    }
  };

  const captureFromLiveCamera = async () => {
    if (!liveVideoRef.current || !liveCanvasRef.current) return;
    const video = liveVideoRef.current;
    const canvas = liveCanvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    ctx.drawImage(video, 0, 0);
    stopLiveCamera();
    setCameraOpen(false);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `receipt-${Date.now()}.jpg`, { type: 'image/jpeg' });
      handleFile(file);
    }, 'image/jpeg', 0.75);
  };

  /* cleanup blob */
  useEffect(() => {
    return () => { if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl); };
  }, [localPreviewUrl]);

  /* ── fetch company GSTIN ── */
  useEffect(() => {
    const gst = String(company?.gstin || '').trim();
    if (gst) setCompanyGstin(gst);
  }, [company?.gstin]);

  useEffect(() => {
    // Fallback: sometimes AuthContext company object lacks gstin.
    try {
      const raw = localStorage.getItem('company');
      const parsed = raw ? JSON.parse(raw) : null;
      const gst = String(parsed?.gstin || '').trim();
      if (gst) setCompanyGstin(gst);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const { data: body } = await api.get('/api/companies/' + companyId);
        const gst = String(body?.company?.gstin || body?.gstin || '').trim();
        if (gst) setCompanyGstin(gst);
      } catch (e) {
        // Keep silent in UI, but log for debugging.
        console.error('[ExpenseForm] Failed to fetch company GSTIN', e?.response?.data || e?.message || e);
      }
    })();
  }, [companyId]);

  /* ── recompute ITC eligibility whenever GSTINs change ── */
  useEffect(() => {
    const vGst = String(vendorGstin || '').trim().toUpperCase();
    const bGst = String(buyerGstin || '').trim().toUpperCase();
    const cGst = String(companyGstin || '').trim().toUpperCase();
    setItcEligible(Boolean(vGst && bGst && cGst && bGst === cGst));
  }, [vendorGstin, buyerGstin, companyGstin]);

  /* ── load existing expense ── */
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const exp = await ExpensesAPI.getById(params.id);
        setVendorId(exp.vendorId || exp.vendor?.id || null);
        setVendorName(exp.vendorName || '');
        setVendorGstin(exp.vendorGstin || '');
        setVendorPhone(exp.vendorPhone || '');
        setVendorEmail(exp.vendorEmail || '');
        setVendorWebsite(exp.vendorWebsite || exp.vendor?.website || '');
        setVendorAddress(typeof exp.vendorAddress === 'object' ? (exp.vendorAddress?.full || JSON.stringify(exp.vendorAddress)) : (exp.vendorAddress || ''));
        setExpNumber(exp.number || '');
        setDate(exp.date || '');
        setDueDate(exp.dueDate || '');
        setCategory(exp.category || 'OTHER');
        setDescription(exp.description || '');
        setNotes(exp.notes || '');
        setReceiptUrl(exp.receiptUrl || '');
        setReceiptS3Key(exp.receiptS3Key || '');
        setOcrRaw(exp.ocrRawResponse || null);
        setOcrConfidence(exp.ocrConfidence || null);
        const latestVendor = latestVendorPayment(exp);
        const latestReimb = latestReimbursement(exp);
        const latestPayment = exp.isReimbursement ? latestReimb : latestVendor;
        setPaymentMethod(latestPayment?.paymentMethod || '');
        setPaymentReference(latestPayment?.paymentReference || '');
        setPayeeName(exp.payeeName || '');
        setAmountPaid(exp.isReimbursement
          ? (exp.reimbursedTotal ?? '')
          : (exp.vendorPaidTotal ?? ''));
        setCurrency(String(exp.currency || 'INR').toUpperCase());
        setExchangeRate(exp.exchangeRate ?? '');
        setConvertedAmount(exp.convertedAmount ?? '');
        setPaymentProofUrl(latestPayment?.proofUrl || exp.statementTransaction?.proofImageUrl || '');
        setStatementProofReference(exp.statementTransaction?.proofReference || '');
        setIsReimbursement(Boolean(exp.isReimbursement));
        setReimbursementApproverId(String(exp.reimbursementApproverId || exp.reimbursementApprover?.id || ''));
        if (String(exp.filedForType || '').toUpperCase() === 'EMPLOYEE') {
          setFiledForType('EMPLOYEE');
          setFiledForId(String(exp.filedForEmployeeId || exp.filedForEmployee?.id || ''));
        } else {
          setFiledForType('USER');
          setFiledForId(String(exp.userId || exp.user?.id || user?.id || ''));
        }
        setBuyerGstin(exp.buyerGstin || '');
        setBuyerName(exp.buyerName || '');
        setBuyerAddress(typeof exp.buyerAddress === 'object' ? (exp.buyerAddress?.full || JSON.stringify(exp.buyerAddress)) : (exp.buyerAddress || ''));
        setBuyerPhone(exp.buyerPhone || '');
        setBuyerEmail(exp.buyerEmail || '');
        if (exp.items && exp.items.length > 0) {
          setItems(exp.items.map((it) => ({
            name: it.name || '', description: it.description || '', hsnsac: it.hsnsac || '',
            quantity: it.quantity || 1, unit: it.unit || 'NOS', price: it.price || 0,
            discountAmount: it.discountAmount || 0, discountPercent: it.discountPercent || 0,
            cgstRate: it.cgstRate || 0, sgstRate: it.sgstRate || 0, igstRate: it.igstRate || 0, cessRate: it.cessRate || 0,
          })));
        }
      } catch (e) { console.error(e); }
    })();
  }, [isEdit, params.id]);

  useEffect(() => {
    if (isEdit || isRecurringTemplateMode) return;
    if (!recurringSeedId) return;
    (async () => {
      try {
        const seed = await RecurringExpensesAPI.occurrenceSeed(recurringSeedId, recurringSeedDate || undefined);
        const exp = seed?.expense || {};
        if (exp.vendorId) {
          setVendorId(exp.vendorId);
        } else if (exp.vendorName) {
          setVendorId(null);
        }
        if (exp.vendorName) {
          setVendorName(exp.vendorName);
        }
        if (exp.date) setDate(exp.date);
        if (exp.dueDate) setDueDate(exp.dueDate);
        if (exp.category) setCategory(exp.category);
        if (exp.description) setDescription(exp.description);
        if (exp.notes) setNotes(exp.notes);
        if (Array.isArray(exp.items) && exp.items.length > 0) {
          setItems(exp.items.map((it) => ({
            name: it.name || '',
            description: it.description || '',
            hsnsac: it.hsnsac || '',
            quantity: it.quantity || 1,
            unit: it.unit || 'NOS',
            price: it.price || 0,
            discountAmount: it.discountAmount || 0,
            discountPercent: it.discountPercent || 0,
            cgstRate: it.cgstRate || 0,
            sgstRate: it.sgstRate || 0,
            igstRate: it.igstRate || 0,
            cessRate: it.cessRate || 0
          })));
        }
      } catch (_) {
        // Keep manual form flow available if seed load fails.
      }
    })();
  }, [isEdit, isRecurringTemplateMode, recurringSeedId, recurringSeedDate]);

  useEffect(() => {
    setUsersLoadedOnce(false);
    setEmployeesLoadedOnce(false);
  }, [companyId]);

  useEffect(() => {
    if (!isReimbursement) return;
    if (!reimbursementApproverId && user?.supervisorId) {
      setReimbursementApproverId(String(user.supervisorId));
    }
    if (usersLoadedOnce || usersLoading) return;
    (async () => {
      setUsersLoading(true);
      try {
        const resp = await CompaniesAPI.users();
        const rows = Array.isArray(resp?.users) ? resp.users : [];
        setCompanyUsers(rows);
      } catch (e) {
        // ignore
      }
      setUsersLoadedOnce(true);
      setUsersLoading(false);
    })();
  }, [isReimbursement, reimbursementApproverId, user?.supervisorId, usersLoadedOnce, usersLoading]);

  useEffect(() => {
    if (!isReimbursement) return;
    if (!companyId) return;
    if (employeesLoadedOnce || employeesLoading) return;
    (async () => {
      setEmployeesLoading(true);
      try {
        const { data } = await api.get(`/api/companies/${companyId}/employees`);
        const rows = Array.isArray(data?.employees) ? data.employees : [];
        setCompanyEmployees(rows);
      } catch (_) {
        setCompanyEmployees([]);
      }
      setEmployeesLoadedOnce(true);
      setEmployeesLoading(false);
    })();
  }, [isReimbursement, companyId, employeesLoadedOnce, employeesLoading]);

  const normalizeText = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).trim();
    if (!s) return '';
    const lowered = s.toLowerCase();
    if (['null', 'na', 'n/a', 'none', '-'].includes(lowered)) return '';
    return s;
  };

  const maybeUseExistingVendor = async () => {
    if (vendorId) return vendorId;
    const name = normalizeText(vendorName);
    if (!name) return null;
    const q = normalizeText(vendorGstin) || name;
    try {
      const rows = await VendorsAPI.list({ companyId, q, limit: 25 });
      const vendors = Array.isArray(rows) ? rows : [];
      if (vendors.length === 0) return null;
      const gstin = normalizeText(vendorGstin).toLowerCase();
      const nameLower = name.toLowerCase();
      const match =
        (gstin ? vendors.find((v) => String(v?.gstin || '').trim().toLowerCase() === gstin) : null) ||
        vendors.find((v) => String(v?.name || '').trim().toLowerCase() === nameLower) ||
        (vendors.length === 1 ? vendors[0] : null);
      if (!match) return null;
      const ok = window.confirm(`Vendor "${match.name}" already exists. Use existing vendor record?`);
      if (!ok) return null;
      setVendorId(match.id);
      setVendorName(match.name || name);
      if (match.gstin) setVendorGstin(match.gstin);
      if (match.phone) setVendorPhone(match.phone);
      if (match.email) setVendorEmail(match.email);
      if (match.address) {
        const addrText = typeof match.address === 'object'
          ? (match.address?.full || JSON.stringify(match.address))
          : String(match.address);
        setVendorAddress(addrText);
      }
      return match.id;
    } catch (e) {
      return null;
    }
  };

  /* ── upload + OCR ── */
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const blobUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(blobUrl);
    setLocalFileType(file.type);
    setExtracting(true);
    setOcrStatus('loading');
    const shouldOptimize = isImageFile(file) && file.size > RECEIPT_OPTIMIZE_THRESHOLD_BYTES;
    setOcrMessage(shouldOptimize ? 'Optimizing receipt image...' : 'Uploading receipt...');
    try {
      const dir = `expenses/${companyId || 'uploads'}`;
      const prepared = await prepareReceiptFile(file);
      const uploadFile = prepared?.file || file;
      const wasOptimized = !!prepared?.optimized;

      if (!wasOptimized || isPdfFile(uploadFile)) {
        setOcrMessage('Uploading receipt...');
      } else {
        setOcrMessage('Uploading optimized receipt...');
      }

      const { data: presigned } = await api.get('/api/upload/presigned-url', { params: { fileType: uploadFile.type, dir } });
      await fetch(presigned.uploadUrl, { method: 'PUT', headers: { 'Content-Type': uploadFile.type }, body: uploadFile });
      setReceiptUrl(presigned.fileUrl);
      const urlParts = new URL(presigned.fileUrl);
      setReceiptS3Key(urlParts.pathname.replace(/^\//, ''));

      setOcrMessage('Extracting data with AI...');
      const result = await ExpensesAPI.extract({ fileUrl: presigned.fileUrl, fileType: uploadFile.type });
      const extracted = result.data;

      if (extracted.vendorName || extracted.vendorGstin || extracted.vendorPhone || extracted.vendorEmail || extracted.vendorAddress) {
        setVendorId(null);
      }
      if (extracted.vendorName) setVendorName(extracted.vendorName);
      if (extracted.vendorGstin) setVendorGstin(extracted.vendorGstin);
      if (extracted.buyerGstin) setBuyerGstin(extracted.buyerGstin);
      if (extracted.buyerName) setBuyerName(extracted.buyerName);
      if (extracted.buyerAddress) setBuyerAddress(extracted.buyerAddress);
      if (extracted.buyerPhone) setBuyerPhone(extracted.buyerPhone);
      if (extracted.buyerEmail) setBuyerEmail(extracted.buyerEmail);
      if (extracted.vendorPhone) setVendorPhone(extracted.vendorPhone);
      if (extracted.vendorEmail) setVendorEmail(extracted.vendorEmail);
      if (extracted.vendorWebsite) setVendorWebsite(extracted.vendorWebsite);
      if (extracted.vendorAddress) setVendorAddress(extracted.vendorAddress);
      if (!isEdit && !String(description || '').trim() && extracted.suggestedDescription) {
        setDescription(String(extracted.suggestedDescription).trim());
      }
      if (extracted.invoiceNumber) setExpNumber(extracted.invoiceNumber);
      if (extracted.date) setDate(extracted.date);
      if (extracted.dueDate) setDueDate(extracted.dueDate);
      if (extracted.category) setCategory(extracted.category);
      if (extracted.paymentMethod) setPaymentMethod(extracted.paymentMethod);
      if (extracted.paymentReference) setPaymentReference(extracted.paymentReference);
      if (extracted.amountPaid !== undefined && extracted.amountPaid !== null) setAmountPaid(extracted.amountPaid);
      if (extracted.currency) setCurrency(String(extracted.currency).toUpperCase());
      if (extracted.items && extracted.items.length > 0) {
        const nextItems = extracted.items.map((it) => ({
          name: it.name || '', description: it.description || '', hsnsac: it.hsnsac || '',
          quantity: it.quantity || 1, unit: it.unit || 'NOS', price: it.price || 0,
          discountAmount: it.discountAmount || 0, discountPercent: it.discountPercent || 0,
          cgstRate: it.cgstRate || 0, sgstRate: it.sgstRate || 0, igstRate: it.igstRate || 0, cessRate: it.cessRate || 0,
        }));
        // Keep OCR items as-is. We will align displayed taxes/totals to OCR document totals
        // (when provided) by allocating doc-level tax amounts across lines.
        setItems(nextItems);
      }
      setOcrRaw(extracted);
      setOcrConfidence(extracted.confidence);
      setOcrStatus('success');
      setOcrMessage(`Extracted ${extracted.items?.length || 0} items with ${Math.round((extracted.confidence || 0) * 100)}% confidence`);
    } catch (err) {
      console.error('[OCR] Error:', err);
      setOcrStatus('error');
      setOcrMessage(err?.response?.data?.error || err.message || 'Extraction failed');
    }
    setExtracting(false);
  }, []);

  /* replace receipt */
  const handleReplace = () => {
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    setLocalPreviewUrl(null);
    setLocalFileType(null);
    setReceiptUrl('');
    setReceiptS3Key('');
    setOcrStatus(null);
    setOcrMessage('');
    setOcrRaw(null);
    setOcrConfidence(null);

    // Clear OCR-populated fields immediately so stale values don't linger.
    setVendorId(null);
    setVendorName('');
    setVendorGstin('');
    setBuyerGstin('');
    setBuyerName('');
    setBuyerAddress('');
    setBuyerPhone('');
    setBuyerEmail('');
    setVendorPhone('');
    setVendorEmail('');
    setVendorWebsite('');
    setVendorAddress('');
    setExpNumber('');
    setDueDate('');
    setCategory('OTHER');
    setPaymentMethod('');
    setPaymentReference('');
    setPayeeName('');
    setAmountPaid('');
    setCurrency('INR');
    setExchangeRate('');
    setConvertedAmount('');
    setItems([emptyLine()]);
    setDescription('');
    setTimeout(() => fileInputRef.current?.click(), 50);
  };

  /* drag & drop */
  const onDrag = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDragIn = (e) => { e.preventDefault(); setDragActive(true); };
  const onDragOut = (e) => { e.preventDefault(); setDragActive(false); };
  const onDrop = (e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]); };

  /* line-item helpers */
  const updateLine = (idx, field, value) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  const removeLine = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const addLine = () => setItems((prev) => [...prev, emptyLine()]);

  /* totals */
  const computedBase = items.map((it) => ({ ...it, ...calcLine(it) }));
  const hasOcrDocTotals =
    ocrStatus === 'success' &&
    ocrRaw &&
    Number.isFinite(Number(ocrRaw?.total)) &&
    Number(ocrRaw?.total) > 0 &&
    (
      Number(ocrRaw?.cgstAmount) ||
      Number(ocrRaw?.sgstAmount) ||
      Number(ocrRaw?.igstAmount) ||
      Number(ocrRaw?.cessAmount) ||
      Number(ocrRaw?.taxableValue)
    );
  const computed = hasOcrDocTotals ? allocateDocTaxAmounts(computedBase, ocrRaw) : computedBase;
  const totals = computed.reduce((acc, it) => {
    const qty = Number(it.quantity) || 0;
    const price = Number(it.price) || 0;
    const gross = qty * price;
    const discPct = (Number(it.discountPercent) / 100) * gross;
    const discAmt = Number(it.discountAmount || 0) + discPct;
    return {
      gross: acc.gross + gross, discount: acc.discount + discAmt,
      taxable: acc.taxable + it.taxableValue, cgst: acc.cgst + it.cgstAmount,
      sgst: acc.sgst + it.sgstAmount, igst: acc.igst + it.igstAmount,
      cess: acc.cess + it.cessAmount, total: acc.total + it.lineTotal,
    };
  }, { gross: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, total: 0 });

  useEffect(() => {
    let cancelled = false;
    const syncForexDefaults = async () => {
      const curr = String(currency || 'INR').toUpperCase();
      if (curr === 'INR') {
        setExchangeRate('');
        setConvertedAmount('');
        return;
      }
      const total = Number(totals.total || 0);
      if (!(total > 0)) return;

      const manualRate = Number(exchangeRate);
      if (Number.isFinite(manualRate) && manualRate > 0) {
        if (!String(convertedAmount || '').trim()) {
          setConvertedAmount((manualRate * total).toFixed(2));
        }
        return;
      }

      const day = (date || new Date().toISOString().slice(0, 10)).slice(0, 10);
      const hosts = ['frankfurter.dev', 'frankfurter.app'];
      for (const host of hosts) {
        try {
          const url = `https://api.${host}/${day}?from=${encodeURIComponent(curr)}&to=INR`;
          const resp = await fetch(url);
          if (!resp.ok) continue;
          const body = await resp.json();
          const rate = Number(body?.rates?.INR);
          if (Number.isFinite(rate) && rate > 0) {
            if (cancelled) return;
            setExchangeRate(rate.toFixed(6));
            if (!String(convertedAmount || '').trim()) {
              setConvertedAmount((rate * total).toFixed(2));
            }
            return;
          }
        } catch (_) {
          // Try next host.
        }
      }
    };
    syncForexDefaults();
    return () => { cancelled = true; };
  }, [currency, date, totals.total, exchangeRate, convertedAmount]);

  const buildBreakupText = () => {
    const tax = totals.cgst + totals.sgst + totals.igst + totals.cess;
    return [
      '--- Amount Breakup ---',
      `Gross: ${fmt(totals.gross)}`,
      `Discount: ${fmt(totals.discount)}`,
      `Taxable: ${fmt(totals.taxable)}`,
      `Tax (CGST+SGST+IGST+Cess): ${fmt(tax)}`,
      `Total: ${fmt(totals.total)}`,
    ].join('\n');
  };

  const buildPaymentBreakupText = () => {
    const lines = [];
    lines.push('--- Payment Breakup ---');
    if (paymentMethod) lines.push(`Payment Method: ${paymentMethod}`);
    if (paymentReference) lines.push(`Reference: ${paymentReference}`);
    if (amountPaid !== '' && amountPaid !== null && amountPaid !== undefined) lines.push(`Amount Paid: ${fmt(amountPaid)}`);
    const pb = Array.isArray(ocrRaw?.paymentBreakdown) ? ocrRaw.paymentBreakdown : [];
    if (pb.length) {
      lines.push('Paid By:');
      for (const p of pb) {
        if (!p) continue;
        const m = p.method || '-';
        const a = p.amount ?? 0;
        lines.push(`- ${m}: ${fmt(a)}`);
      }
    }
    return lines.join('\n');
  };

  const mergeSection = (base, header, bodyLines) => {
    const text = String(base || '').trim();
    const body = bodyLines.trim();
    if (!body) return text;
    const start = text.indexOf(header);
    if (start === -1) return [text, body].filter(Boolean).join('\n\n');
    // Replace existing section until next section marker or end
    const nextIdx = text.indexOf('---', start + header.length);
    if (nextIdx === -1) return [text.slice(0, start).trim(), body].filter(Boolean).join('\n\n');
    return [text.slice(0, start).trim(), body, text.slice(nextIdx).trim()].filter(Boolean).join('\n\n');
  };

  /* save */
  const handleSave = async (submitStatus = 'DRAFT') => {
    if (saving) return;
    if (isRecurringTemplateMode) {
      setSaving(true);
      try {
        const resolvedVendorId = await maybeUseExistingVendor();
        const payload = {
          companyId,
          name: String(recurringName || description || vendorName || 'Recurring Expense').trim(),
          billingInterval: recurringBillingInterval,
          intervalCount: Math.max(1, Number(recurringIntervalCount || 1)),
          billingAnchor: recurringBillingAnchor,
          dayOfMonth: recurringBillingAnchor === 'DAY_OF_MONTH'
            ? (recurringDayOfMonth ? Number(recurringDayOfMonth) : null)
            : null,
          startDate: recurringStartDate || new Date().toISOString().slice(0, 10),
          endDate: recurringEndDate || null,
          category,
          amount: Number(recurringAmount || totals.total || 0),
          currency: 'INR',
          vendorId: resolvedVendorId || vendorId || null,
          vendorName: vendorName || null,
          description: description || null,
          notes: notes || null
        };
        await RecurringExpensesAPI.create(payload);
        navigate('/expenses');
      } catch (err) {
        alert(err?.response?.data?.error || 'Save failed');
      }
      setSaving(false);
      return;
    }
    // Reference / Transaction ID is required when payment method or amount is entered
    const hasPaymentMethod = Boolean(paymentMethod && paymentMethod.trim());
    const hasAmountPaid = Number(amountPaid) > 0;
    if ((hasPaymentMethod || hasAmountPaid) && !String(paymentReference || '').trim()) {
      alert('Reference / Transaction ID is required when Payment Method or Amount Paid is entered.');
      return;
    }
    // If user is submitting for approval with full payment details (and not reimbursement),
    // ask for confirmation whether to mark as paid to vendor.
    if (
      submitStatus === 'PENDING' &&
      !isReimbursement &&
      paymentMethod &&
      paymentReference &&
      Number(amountPaid) > 0
    ) {
      setPendingSubmitStatus(submitStatus);
      setPaidConfirmOpen(true);
      return;
    }
    setSaving(true);
    try {
      const resolvedVendorId = await maybeUseExistingVendor();
      if (submitStatus === 'PENDING' && isReimbursement && !Number(reimbursementApproverId)) {
        alert('Select an approver for this reimbursement.');
        setSaving(false);
        return;
      }
      if (submitStatus === 'PENDING' && isReimbursement) {
        if (!paymentMethod || !paymentReference || !(Number(amountPaid) > 0)) {
          alert('For reimbursements, fill Payment Method, Reference, and Amount Paid.');
          setSaving(false);
          return;
        }
      }
      const finalStatus = submitStatus;
      const autoDescFallback = (() => {
        const v = String(vendorName || '').trim();
        const num = String(expNumber || '').trim();
        const amt = Number(amountPaid) > 0 ? Number(amountPaid) : Number(totals.total || 0);
        const parts = [
          v || 'Expense',
          num ? `#${num}` : null,
          amt ? `₹${Number(amt).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : null,
          date ? String(date) : null
        ].filter(Boolean);
        return parts.join(' • ');
      })();

      let finalDescription = description;
      if (!isEdit && !String(description || '').trim()) {
        try {
          const ai = await ExpensesAPI.generateDescription({
            vendorName,
            items: items.map((it) => ({ name: it.name, description: it.description }))
          });
          finalDescription = String(ai?.description || '').trim() || autoDescFallback;
        } catch (e) {
          finalDescription = autoDescFallback;
        }
      }
      const breakup = buildBreakupText();
      const paymentBreakup = buildPaymentBreakupText();
      const notesWithAmount = mergeSection(notes, '--- Amount Breakup ---', breakup);
      const finalNotes = mergeSection(notesWithAmount, '--- Payment Breakup ---', paymentBreakup);
      const payload = {
        companyId,
        filedForType,
        userId: filedForType === 'USER' && filedForId ? Number(filedForId) : null,
        filedForEmployeeId: filedForType === 'EMPLOYEE' && filedForId ? Number(filedForId) : null,
        vendorId: resolvedVendorId || vendorId || null,
        vendorName, vendorGstin, vendorPhone, vendorEmail, vendorWebsite,
        vendorAddress, number: expNumber, date, dueDate: dueDate || null,
        currency: String(currency || 'INR').toUpperCase(),
        exchangeRate: String(currency || 'INR').toUpperCase() !== 'INR' && Number(exchangeRate) > 0 ? Number(exchangeRate) : null,
        convertedAmount: String(currency || 'INR').toUpperCase() !== 'INR' && Number(convertedAmount) > 0 ? Number(convertedAmount) : null,
        category, description: finalDescription, notes: finalNotes, items,
        isReimbursement: Boolean(isReimbursement),
        reimbursementApproverId: isReimbursement && Number(reimbursementApproverId) ? Number(reimbursementApproverId) : null,
        paymentMethod: paymentMethod || null,
        paymentReference: paymentReference || null,
        payeeName: payeeName || null,
        amountPaid: amountPaid === '' ? null : Number(amountPaid),
        receiptUrl, receiptS3Key, ocrRawResponse: ocrRaw, ocrConfidence,
        buyerGstin: buyerGstin || null,
        buyerName: buyerName || null,
        buyerAddress: buyerAddress || null,
        buyerPhone: buyerPhone || null,
        buyerEmail: buyerEmail || null,
        itcEligible,
        status: finalStatus,
        ...(!isEdit && {
          recurringExpenseId: recurringSeedId ? Number(recurringSeedId) : null,
          occurrenceDate: recurringSeedDate || null
        })
      };
      if (isEdit) { await ExpensesAPI.update(params.id, payload); }
      else { await ExpensesAPI.create(payload); }
      navigate('/expenses');
    } catch (err) {
      alert(err?.response?.data?.error || 'Save failed');
    }
    setSaving(false);
  };

  const confirmPaidAndSave = async (markPaid) => {
    if (isRecurringTemplateMode) return;
    const submitStatus = pendingSubmitStatus || 'PENDING';
    setPaidConfirmOpen(false);
    setPendingSubmitStatus(null);
    setSaving(true);
    try {
      const resolvedVendorId = await maybeUseExistingVendor();
      const finalStatus = markPaid ? 'PAID' : submitStatus;
      const autoDescFallback = (() => {
        const v = String(vendorName || '').trim();
        const num = String(expNumber || '').trim();
        const amt = Number(amountPaid) > 0 ? Number(amountPaid) : Number(totals.total || 0);
        const parts = [
          v || 'Expense',
          num ? `#${num}` : null,
          amt ? `₹${Number(amt).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : null,
          date ? String(date) : null
        ].filter(Boolean);
        return parts.join(' • ');
      })();

      let finalDescription = description;
      if (!isEdit && !String(description || '').trim()) {
        try {
          const ai = await ExpensesAPI.generateDescription({
            vendorName,
            items: items.map((it) => ({ name: it.name, description: it.description }))
          });
          finalDescription = String(ai?.description || '').trim() || autoDescFallback;
        } catch (e) {
          finalDescription = autoDescFallback;
        }
      }
      const breakup = buildBreakupText();
      const paymentBreakup = buildPaymentBreakupText();
      const notesWithAmount = mergeSection(notes, '--- Amount Breakup ---', breakup);
      const finalNotes = mergeSection(notesWithAmount, '--- Payment Breakup ---', paymentBreakup);
      const payload = {
        companyId,
        filedForType,
        userId: filedForType === 'USER' && filedForId ? Number(filedForId) : null,
        filedForEmployeeId: filedForType === 'EMPLOYEE' && filedForId ? Number(filedForId) : null,
        vendorId: resolvedVendorId || vendorId || null,
        vendorName, vendorGstin, vendorPhone, vendorEmail, vendorWebsite,
        vendorAddress, number: expNumber, date, dueDate: dueDate || null,
        currency: String(currency || 'INR').toUpperCase(),
        exchangeRate: String(currency || 'INR').toUpperCase() !== 'INR' && Number(exchangeRate) > 0 ? Number(exchangeRate) : null,
        convertedAmount: String(currency || 'INR').toUpperCase() !== 'INR' && Number(convertedAmount) > 0 ? Number(convertedAmount) : null,
        category, description: finalDescription, notes: finalNotes, items,
        isReimbursement: Boolean(isReimbursement),
        reimbursementApproverId: isReimbursement && Number(reimbursementApproverId) ? Number(reimbursementApproverId) : null,
        paymentMethod: paymentMethod || null,
        paymentReference: paymentReference || null,
        payeeName: payeeName || null,
        amountPaid: amountPaid === '' ? null : Number(amountPaid),
        receiptUrl, receiptS3Key, ocrRawResponse: ocrRaw, ocrConfidence,
        buyerGstin: buyerGstin || null,
        buyerName: buyerName || null,
        buyerAddress: buyerAddress || null,
        buyerPhone: buyerPhone || null,
        buyerEmail: buyerEmail || null,
        itcEligible,
        status: finalStatus,
        ...(!isEdit && {
          recurringExpenseId: recurringSeedId ? Number(recurringSeedId) : null,
          occurrenceDate: recurringSeedDate || null
        })
      };
      if (isEdit) { await ExpensesAPI.update(params.id, payload); }
      else { await ExpensesAPI.create(payload); }
      navigate('/expenses');
    } catch (err) {
      alert(err?.response?.data?.error || 'Save failed');
    }
    setSaving(false);
  };

  const convertToRecurringTemplate = async () => {
    if (saving || isRecurringTemplateMode) return;
    setSaving(true);
    try {
      const resolvedVendorId = await maybeUseExistingVendor();
      const startDate = date || new Date().toISOString().slice(0, 10);
      const payload = {
        companyId,
        name: String(description || vendorName || expNumber || 'Recurring Expense').trim(),
        billingInterval: 'MONTH',
        intervalCount: 1,
        billingAnchor: 'ANNIVERSARY',
        dayOfMonth: null,
        startDate,
        endDate: null,
        category: category || 'OTHER',
        amount: Number(totals.total || 0),
        currency: 'INR',
        vendorId: resolvedVendorId || vendorId || null,
        vendorName: vendorName || null,
        description: description || null,
        notes: notes || null
      };
      await RecurringExpensesAPI.create(payload);
      navigate('/expenses');
    } catch (err) {
      alert(err?.response?.data?.error || 'Convert to recurring failed');
    }
    setSaving(false);
  };

  /* ── helper: is it a PDF? ── */
  const isPdf = localFileType === 'application/pdf' ||
    (!localFileType && receiptUrl && receiptUrl.toLowerCase().endsWith('.pdf'));
  const previewUrl = localPreviewUrl || receiptUrl;

  /* ────── RENDER ────── */
  return (
    <div className="page-content">
      <style>{`
        @keyframes bnSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: bnSpin 1s linear infinite;
        }
      `}</style>
      <fieldset disabled={extracting} style={{ border: 'none', padding: 0, margin: 0 }}>
      {/* Header */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={() => navigate('/expenses')}><FiArrowLeft size={20} /></button>
        <h1 style={s.title}>
          {isEdit ? 'Edit Expense' : (isRecurringTemplateMode ? 'New Recurring Expense' : 'New Expense')}
        </h1>
      </div>

      {/* Hidden file input (shared) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,image/webp,application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = ''; }}
      />
      {/* Hidden camera input (mobile-friendly) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = ''; }}
      />

      {/* ─── Card 1: Upload / Preview (LHS) + Vendor Details (RHS) ─── */}
      {!isRecurringTemplateMode && (
      <div style={s.card}>
        <div style={s.splitRow}>
          {/* LEFT - Upload or Preview */}
          <div style={s.splitLeft}>
            <div style={s.cardTitle}>Receipt / Invoice</div>

            {!hasPreview ? (
              /* Dropzone */
              <div
                style={s.dropzone(dragActive)}
                onDragEnter={onDragIn} onDragLeave={onDragOut} onDragOver={onDrag} onDrop={onDrop}
                onClick={() => { if (!extracting) fileInputRef.current?.click(); }}
              >
                <FiUploadCloud size={36} color={dragActive ? colors.accent500 : colors.stone400} />
                <div style={s.dropzoneText}>
                  {extracting ? 'Processing...' : 'Drag & drop a receipt or click to browse'}
                </div>
                <div style={{ fontSize: 12, color: colors.stone400, marginTop: 4 }}>Supports JPG, PNG, PDF</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ ...s.smallBtn, height: 30, minHeight: 30 }}
                    onClick={(e) => { e.stopPropagation(); startLiveCamera(); }}
                    disabled={extracting}
                  >
                    Use camera
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ ...s.smallBtn, height: 30, minHeight: 30 }}
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    disabled={extracting}
                  >
                    Browse
                  </button>
                </div>
                {extracting && (
                  <div style={s.loadingOverlay}>
                    <FiLoader size={18} className="spin" />
                    <div style={s.loadingText}>Extracting…</div>
                  </div>
                )}
              </div>
            ) : (
              /* Preview (replaces dropzone) */
              <div style={s.previewWrap}>
                <div style={s.previewHeader}>
                  <span style={s.previewLabel}>{isPdf ? 'PDF Preview' : 'Image Preview'} </span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {previewUrl && !isPdf && (
                      <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{ ...s.smallBtn, textDecoration: 'none' }}>
                        Open
                      </a>
                    )}
                    {previewUrl && isPdf && (
                      <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{ ...s.smallBtn, textDecoration: 'none' }}>
                        <FiFileText size={12} /> Open
                      </a>
                    )}
                    <button className="btn btn-secondary" style={s.smallBtn} onClick={handleReplace}>
                      <FiRefreshCw size={12} /> Replace
                    </button>
                  </div>
                </div>
                {isPdf ? (
                  <iframe src={previewUrl} style={s.pdfEmbed} title="Receipt PDF" />
                ) : (
                  <img
                    src={previewUrl} alt="Receipt" style={s.previewImg}
                    onClick={() => setLightboxOpen(true)} title="Click to enlarge"
                  />
                )}
                {extracting && (
                  <div style={s.loadingOverlay}>
                    <FiLoader size={18} className="spin" />
                    <div style={s.loadingText}>Extracting…</div>
                  </div>
                )}
              </div>
            )}

            {/* OCR status */}
            {ocrStatus && (
              <div style={s.ocrBanner(ocrStatus)}>
                {ocrStatus === 'loading' && <FiLoader size={14} className="spin" />}
                {ocrStatus === 'success' && <FiCheck size={14} />}
                {ocrMessage}
              </div>
            )}

           
          </div>

          {/* RIGHT - Vendor Details */}
          <div style={s.splitRight}>
            <div style={s.cardTitle}>Vendor Details</div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Vendor Name</label>
                <input
                  style={s.input}
                  value={vendorName}
                  onChange={(e) => { setVendorId(null); setVendorName(e.target.value); }}
                  placeholder="Vendor name"
                />
              </div>
            </div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>GSTIN</label>
                <input
                  style={s.input}
                  value={vendorGstin}
                  onChange={(e) => { setVendorId(null); setVendorGstin(e.target.value); }}
                  placeholder="15-char GSTIN"
                />
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>Invoice / Receipt #</label>
                <input style={s.input} value={expNumber} onChange={(e) => setExpNumber(e.target.value)} placeholder="Number" />
              </div>
            </div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Phone</label>
                <input
                  style={s.input}
                  value={vendorPhone}
                  onChange={(e) => { setVendorId(null); setVendorPhone(e.target.value); }}
                  placeholder="Phone number"
                />
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>Email</label>
                <input
                  style={s.input}
                  type="email"
                  value={vendorEmail}
                  onChange={(e) => { setVendorId(null); setVendorEmail(e.target.value); }}
                  placeholder="email@vendor.com"
                />
              </div>
            </div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Website</label>
                <input
                  style={s.input}
                  value={vendorWebsite}
                  onChange={(e) => { setVendorId(null); setVendorWebsite(e.target.value); }}
                  placeholder="https://vendor.com"
                />
              </div>
            </div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Address</label>
                <textarea
                  style={{ ...s.input, minHeight: 60, resize: 'vertical' }}
                  value={vendorAddress}
                  onChange={(e) => { setVendorId(null); setVendorAddress(e.target.value); }}
                  placeholder="Full vendor address"
                />
              </div>
            </div>
            <div style={{ marginTop: 6, marginBottom: 6, fontSize: 12, fontWeight: 700, color: colors.stone700 }}>
              Billed To
            </div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Name</label>
                <input
                  style={s.input}
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  placeholder="Buyer / billed-to name"
                />
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>GSTIN</label>
                <input
                  style={s.input}
                  value={buyerGstin}
                  onChange={(e) => setBuyerGstin(e.target.value)}
                  placeholder="Buyer GSTIN"
                />
              </div>
            </div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Phone</label>
                <input
                  style={s.input}
                  value={buyerPhone}
                  onChange={(e) => setBuyerPhone(e.target.value)}
                  placeholder="Buyer phone"
                />
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>Email</label>
                <input
                  style={s.input}
                  type="email"
                  value={buyerEmail}
                  onChange={(e) => setBuyerEmail(e.target.value)}
                  placeholder="buyer@company.com"
                />
              </div>
            </div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Address</label>
                <textarea
                  style={{ ...s.input, minHeight: 60, resize: 'vertical' }}
                  value={buyerAddress}
                  onChange={(e) => setBuyerAddress(e.target.value)}
                  placeholder="Billed-to address"
                />
              </div>
            </div>
             {/* ITC eligibility */}
             {ocrStatus === 'success' && vendorGstin && (
              <div style={s.ocrBanner(itcEligible ? 'success' : 'error')}>
                <FiCheck size={14} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div>
                    {itcEligible
                      ? 'Eligible for Input Tax Credit (ITC)'
                      : buyerGstin
                        ? (companyGstin ? 'Not eligible for ITC - buyer GSTIN does not match company GSTIN' : 'Not eligible for ITC - company GSTIN not set')
                        : 'Not eligible for ITC - buyer GSTIN not found on invoice'}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.85 }}>
                    Vendor GSTIN: {String(vendorGstin || '-').trim() || '-'} • Buyer GSTIN: {String(buyerGstin || '-').trim() || '-'} • Company GSTIN: {String(companyGstin || '-').trim() || '-'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ─── Card 2: Expense Details / Recurring Template ─── */}
      <div style={s.card}>
        <div style={s.cardTitle}>{isRecurringTemplateMode ? 'Recurring Template Details' : 'Expense Details'}</div>
        {!isRecurringTemplateMode && (
          <div style={s.row}>
            <div style={s.field(1)}>
              <label style={s.label}>Reimbursement</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 38 }}>
                <input
                  type="checkbox"
                  checked={isReimbursement}
                  onChange={(e) => setIsReimbursement(e.target.checked)}
                />
                <div style={{ fontSize: 14, color: colors.stone700, fontWeight: 600 }}>
                  File as reimbursement
                </div>
              </div>
            </div>
            {isReimbursement && (
              <>
                <div style={s.field(1)}>
                  <label style={s.label}>Filed for (on behalf of)</label>
                  <select
                    style={s.select}
                    value={filedForId ? `${filedForType}:${filedForId}` : ''}
                    onChange={(e) => {
                      const v = String(e.target.value || '');
                      const idx = v.indexOf(':');
                      if (idx === -1) return;
                      const t = v.slice(0, idx).toUpperCase();
                      const id = v.slice(idx + 1);
                      const nextType = t === 'EMPLOYEE' ? 'EMPLOYEE' : 'USER';
                      setFiledForType(nextType);
                      setFiledForId(id);

                      // If the selected "Filed for" person is currently chosen as approver,
                      // clear approver to avoid selecting the same person.
                      try {
                        const filedForUserId = (() => {
                          if (nextType === 'USER') return id ? Number(id) : null;
                          const empId = id ? Number(id) : null;
                          if (!empId) return null;
                          const emp = (companyEmployees || []).find((x) => Number(x?.id) === empId);
                          return emp?.userId != null ? Number(emp.userId) : null;
                        })();
                        if (filedForUserId && String(reimbursementApproverId) === String(filedForUserId)) {
                          setReimbursementApproverId('');
                        }
                      } catch (_) { /* ignore */ }
                    }}
                    disabled={usersLoading || employeesLoading}
                  >
                    <option value="">{(usersLoading || employeesLoading) ? 'Loading…' : '- Select person -'}</option>
                    <optgroup label="Users">
                      {(() => {
                        const seen = new Set();
                        const rows = [];
                        const push = (u, prefix = '') => {
                          if (!u?.id) return;
                          const key = String(u.id);
                          if (seen.has(key)) return;
                          seen.add(key);
                          const name = `${[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || ''}`.trim();
                          rows.push({ id: u.id, label: `${prefix}${name || `User ${u.id}`}` });
                        };
                        push(user, '(Me) ');
                        (companyUsers || []).forEach((u) => push(u, ''));
                        return rows;
                      })().map((u) => (
                        <option key={`USER:${u.id}`} value={`USER:${u.id}`}>{u.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Employees">
                      {(() => {
                        const norm = (v) => String(v || '').trim().toLowerCase();
                        const userIds = new Set();
                        const userEmails = new Set();

                        const allUsers = [user, ...(companyUsers || [])].filter(Boolean);
                        allUsers.forEach((u) => {
                          if (u?.id != null) userIds.add(Number(u.id));
                          const em = norm(u?.email);
                          if (em) userEmails.add(em);
                        });

                        const selectedEmployeeId =
                          filedForType === 'EMPLOYEE' && filedForId ? Number(filedForId) : null;

                        return (companyEmployees || []).filter((e) => {
                          if (!e?.id) return false;
                          if (selectedEmployeeId && Number(e.id) === selectedEmployeeId) return true;
                          if (e.userId != null && userIds.has(Number(e.userId))) return false;
                          const em = norm(e.email);
                          if (em && userEmails.has(em)) return false;
                          return true;
                        });
                      })().map((e) => {
                        const name = `${[e.firstName, e.lastName].filter(Boolean).join(' ') || e.email || ''}`.trim();
                        return (
                          <option key={`EMPLOYEE:${e.id}`} value={`EMPLOYEE:${e.id}`}>
                            {name || `Employee ${e.id}`}
                          </option>
                        );
                      })}
                    </optgroup>
                  </select>
                </div>
                <div style={s.field(1)}>
                  <label style={s.label}>Approver</label>
                  <select
                    style={s.select}
                    value={reimbursementApproverId}
                    onChange={(e) => setReimbursementApproverId(e.target.value)}
                    disabled={usersLoading}
                  >
                    <option value="">{usersLoading ? 'Loading…' : '- Select approver -'}</option>
                    {companyUsers.filter((u) => {
                      try {
                        const filedForUserId = (() => {
                          if (filedForType === 'USER') return filedForId ? Number(filedForId) : null;
                          const empId = filedForId ? Number(filedForId) : null;
                          if (!empId) return null;
                          const emp = (companyEmployees || []).find((x) => Number(x?.id) === empId);
                          return emp?.userId != null ? Number(emp.userId) : null;
                        })();
                        if (!filedForUserId) return true;
                        return Number(u?.id) !== Number(filedForUserId);
                      } catch (_) {
                        return true;
                      }
                    }).map((u) => {
                      const name = `${[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || ''}`.trim();
                      const depts = Array.isArray(u.departments) ? u.departments : [];
                      const primary = depts.find((d) => d?.isPrimary) || depts[0] || null;
                      const deptLabel = primary?.name ? ` • ${primary.name}` : '';
                      return (
                        <option key={u.id} value={u.id}>
                          {name}{deptLabel}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </>
            )}
          </div>
        )}
        <div style={s.row}>
          {!isRecurringTemplateMode && (
            <>
              <div style={s.field(1)}>
                <label style={s.label}>Date</label>
                <input style={s.input} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>Due Date</label>
                <input style={s.input} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
            </>
          )}
          <div style={s.field(1)}>
            <label style={s.label}>Category</label>
            <select style={s.select} value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          {!isRecurringTemplateMode && (
            <div style={s.field(1)}>
              <label style={s.label}>Currency</label>
              <select style={s.select} value={String(currency || 'INR').toUpperCase()} onChange={(e) => setCurrency(String(e.target.value || 'INR').toUpperCase())}>
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="AED">AED</option>
              </select>
            </div>
          )}
        </div>
        <div style={s.row}>
          <div style={s.field(2)}>
            <label style={s.label}>Description</label>
            <input style={s.input} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description" />
          </div>
          <div style={s.field(1)}>
            <label style={s.label}>Notes</label>
            <textarea style={{ ...s.input, minHeight: 60, resize: 'vertical' }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes" />
          </div>
        </div>
        {!isRecurringTemplateMode && String(currency || 'INR').toUpperCase() !== 'INR' && (
          <>
            <div style={{ ...s.cardTitle, marginTop: 8 }}>Foreign Currency</div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Exchange Rate ({String(currency || '').toUpperCase()} to INR)</label>
                <input
                  style={s.input}
                  type="number"
                  min="0"
                  step="any"
                  value={exchangeRate}
                  onChange={(e) => {
                    const next = e.target.value;
                    setExchangeRate(next);
                    const rateNum = Number(next);
                    const totalNum = Number(totals.total || 0);
                    if (Number.isFinite(rateNum) && rateNum > 0 && totalNum > 0) {
                      setConvertedAmount((rateNum * totalNum).toFixed(2));
                    }
                  }}
                  placeholder="e.g. 84.250000"
                />
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>Converted Amount (INR)</label>
                <input
                  style={s.input}
                  type="number"
                  min="0"
                  step="any"
                  value={convertedAmount}
                  onChange={(e) => setConvertedAmount(e.target.value)}
                  placeholder="INR amount from card/bank conversion"
                />
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>Invoice Total ({String(currency || '').toUpperCase()})</label>
                <div style={{ ...s.input, background: colors.stone50, color: colors.stone700, minHeight: 40, display: 'flex', alignItems: 'center' }}>
                  {fmt(totals.total)}
                </div>
              </div>
            </div>
          </>
        )}
        {isRecurringTemplateMode && (
          <>
            <div style={{ ...s.cardTitle, marginTop: 8 }}>Recurring Settings</div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Template Name</label>
                <input
                  style={s.input}
                  value={recurringName}
                  onChange={(e) => setRecurringName(e.target.value)}
                  placeholder="Rent, EMI, Salary, etc."
                />
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>Amount</label>
                <input
                  style={s.input}
                  type="number"
                  min="0"
                  step="any"
                  value={recurringAmount}
                  onChange={(e) => setRecurringAmount(e.target.value)}
                  placeholder={String(Number(totals.total || 0))}
                />
              </div>
              <div style={s.field(1)} />
            </div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Vendor Name (optional)</label>
                <input
                  style={s.input}
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  placeholder="Landlord, bank, payroll vendor, etc."
                />
              </div>
              <div style={s.field(2)} />
            </div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Frequency</label>
                <select style={s.select} value={recurringBillingInterval} onChange={(e) => setRecurringBillingInterval(e.target.value)}>
                  <option value="WEEK">Weekly</option>
                  <option value="MONTH">Monthly</option>
                  <option value="QUARTER">Quarterly</option>
                  <option value="YEAR">Yearly</option>
                  <option value="CUSTOM">Custom (months)</option>
                </select>
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>Every</label>
                <input
                  style={s.input}
                  type="number"
                  min="1"
                  step="1"
                  value={recurringIntervalCount}
                  onChange={(e) => setRecurringIntervalCount(e.target.value)}
                />
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>Anchor</label>
                <select style={s.select} value={recurringBillingAnchor} onChange={(e) => setRecurringBillingAnchor(e.target.value)}>
                  <option value="ANNIVERSARY">Anniversary date</option>
                  <option value="DAY_OF_MONTH">Day of month</option>
                </select>
              </div>
            </div>
            <div style={s.row}>
              <div style={s.field(1)}>
                <label style={s.label}>Start Date</label>
                <input style={s.input} type="date" value={recurringStartDate} onChange={(e) => setRecurringStartDate(e.target.value)} />
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>End Date (optional)</label>
                <input style={s.input} type="date" value={recurringEndDate} onChange={(e) => setRecurringEndDate(e.target.value)} />
              </div>
              <div style={s.field(1)}>
                <label style={s.label}>Day of Month</label>
                <input
                  style={s.input}
                  type="number"
                  min="1"
                  max="28"
                  step="1"
                  value={recurringDayOfMonth}
                  onChange={(e) => setRecurringDayOfMonth(e.target.value)}
                  disabled={recurringBillingAnchor !== 'DAY_OF_MONTH'}
                  placeholder="1-28"
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* ─── Card 3: Line Items ─── */}
      <div style={s.card}>
        <div style={s.cardTitle}>Line Items</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, minWidth: 160 }}>Name</th>
                <th style={{ ...s.th, minWidth: 80 }}>HSN/SAC</th>
                <th style={{ ...s.th, width: 60 }}>Qty</th>
                <th style={{ ...s.th, width: 60 }}>Unit</th>
                <th style={{ ...s.th, width: 80 }}>Price</th>
                <th style={{ ...s.th, width: 70 }}>Disc Amt</th>
                <th style={{ ...s.th, width: 60 }}>Disc %</th>
                <th style={{ ...s.th, width: 60 }}>CGST%</th>
                <th style={{ ...s.th, width: 60 }}>SGST%</th>
                <th style={{ ...s.th, width: 60 }}>IGST%</th>
                <th style={{ ...s.th, width: 80 }}>Taxable</th>
                <th style={{ ...s.th, width: 80 }}>Total</th>
                <th style={{ ...s.th, width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {computed.map((line, idx) => (
                <tr key={idx}>
                  <td style={s.td}><input style={s.lineInput} value={line.name} onChange={(e) => updateLine(idx, 'name', e.target.value)} placeholder="Item name" /></td>
                  <td style={s.td}><input style={s.lineInput} value={line.hsnsac} onChange={(e) => updateLine(idx, 'hsnsac', e.target.value)} placeholder="HSN" /></td>
                  <td style={s.td}><input style={s.lineNum} type="number" min="0" step="any" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} /></td>
                  <td style={s.td}>
                    {(() => {
                      const unit = String(line.unit || '').trim();
                      const inDefault = defaultUnitOptions.includes(unit);
                      return (
                        <>
                          <select
                            style={{ ...s.lineInput, width: 92 }}
                            value={inDefault ? unit : (unit ? 'CUSTOM' : '')}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === 'CUSTOM') {
                                updateLine(idx, 'unit', '');
                                return;
                              }
                              updateLine(idx, 'unit', v);
                            }}
                          >
                            <option value="">-</option>
                            {defaultUnitOptions.map((u) => <option key={u} value={u}>{u}</option>)}
                            <option value="CUSTOM">Custom…</option>
                          </select>
                          {!inDefault && (
                            <input
                              style={{ ...s.lineInput, marginTop: 6, width: 92 }}
                              value={unit}
                              onChange={(e) => updateLine(idx, 'unit', e.target.value)}
                              placeholder="Custom"
                            />
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td style={s.td}><input style={s.lineNum} type="number" min="0" step="any" value={line.price} onChange={(e) => updateLine(idx, 'price', e.target.value)} /></td>
                  <td style={s.td}><input style={s.lineNum} type="number" min="0" step="any" value={line.discountAmount} onChange={(e) => updateLine(idx, 'discountAmount', e.target.value)} /></td>
                  <td style={s.td}><input style={s.lineNum} type="number" min="0" max="100" step="any" value={line.discountPercent} onChange={(e) => updateLine(idx, 'discountPercent', e.target.value)} /></td>
                  <td style={s.td}><input style={s.lineNum} type="number" min="0" step="any" value={line.cgstRate} onChange={(e) => updateLine(idx, 'cgstRate', e.target.value)} /></td>
                  <td style={s.td}><input style={s.lineNum} type="number" min="0" step="any" value={line.sgstRate} onChange={(e) => updateLine(idx, 'sgstRate', e.target.value)} /></td>
                  <td style={s.td}><input style={s.lineNum} type="number" min="0" step="any" value={line.igstRate} onChange={(e) => updateLine(idx, 'igstRate', e.target.value)} /></td>
                  <td style={{ ...s.td, fontWeight: 600, fontSize: 13 }}>{fmt(line.taxableValue)}</td>
                  <td style={{ ...s.td, fontWeight: 700, fontSize: 13 }}>{fmt(line.lineTotal)}</td>
                  <td style={s.td}>
                    {items.length > 1 && (
                      <button style={s.removeBtn} onClick={() => removeLine(idx)}><FiTrash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="btn btn-secondary" style={s.addLineBtn} onClick={addLine}><FiPlus size={14} /> Add Line</button>

        {/* Totals */}
        <div style={s.totalsRow}>
          {totals.discount > 0 && <div><div style={s.totalLabel}>Discount</div><div style={{ ...s.totalValue, color: colors.rose600 }}>-{fmt(totals.discount)}</div></div>}
          <div><div style={s.totalLabel}>Taxable</div><div style={s.totalValue}>{fmt(totals.taxable)}</div></div>
          {totals.cgst > 0 && <div><div style={s.totalLabel}>CGST</div><div style={s.totalValue}>{fmt(totals.cgst)}</div></div>}
          {totals.sgst > 0 && <div><div style={s.totalLabel}>SGST</div><div style={s.totalValue}>{fmt(totals.sgst)}</div></div>}
          {totals.igst > 0 && <div><div style={s.totalLabel}>IGST</div><div style={s.totalValue}>{fmt(totals.igst)}</div></div>}
          {totals.cess > 0 && <div><div style={s.totalLabel}>Cess</div><div style={s.totalValue}>{fmt(totals.cess)}</div></div>}
          <div><div style={s.totalLabel}>Grand Total</div><div style={{ ...s.totalValue, fontSize: 18 }}>{fmt(totals.total)}</div></div>
        </div>
      </div>

      {/* ─── Card 4: Payment Details ─── */}
      {!isRecurringTemplateMode && (
      <div style={s.card}>
        <div style={s.cardTitle}>Payment Details</div>
        <div style={s.row}>
          <div style={s.field(1)}>
            <label style={s.label}>Payment Method</label>
            <select style={s.select} value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div style={s.field(2)}>
            <label style={s.label}>
              Reference / Transaction ID
              {(paymentMethod && paymentMethod.trim()) || Number(amountPaid) > 0 ? (
                <span style={{ color: colors.rose600 }}> *</span>
              ) : null}
            </label>
            <input style={s.input} value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} placeholder="UPI ID, bank account, cheque #, etc." />
          </div>
        </div>
        <div style={s.row}>
          <div style={s.field(3)}>
            <label style={s.label}>Payee Name (actual payer)</label>
            <input
              style={s.input}
              value={payeeName}
              onChange={(e) => setPayeeName(e.target.value)}
              placeholder="Tom Paiva paid from HDFC a/c XXX"
            />
          </div>
        </div>
        <div style={s.row}>
          <div style={s.field(1)}>
            <label style={s.label}>Amount Paid</label>
            <input style={s.input} type="number" min="0" step="any" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} placeholder="0.00" />
          </div>
          <div style={s.field(2)}>
            <label style={s.label}>Breakup (auto)</label>
            <div style={{ ...s.input, background: colors.stone50, color: colors.stone700, minHeight: 40, display: 'flex', alignItems: 'center' }}>
              Discount {fmt(totals.discount)} • Taxable {fmt(totals.taxable)} • Tax {fmt((totals.cgst + totals.sgst + totals.igst + totals.cess))} • Total {fmt(totals.total)}
            </div>
          </div>
        </div>
        {paymentProofUrl && (
          <div style={{ ...s.row, marginTop: 4 }}>
            <div style={s.field(3)}>
              <label style={s.label}>Statement Payment Proof</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', border: `1px solid ${colors.stone200}`, borderRadius: 10, padding: 10, background: '#fff' }}>
                <a href={paymentProofUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
                  <img
                    src={paymentProofUrl}
                    alt="Statement transaction proof"
                    style={{ width: 180, height: 110, objectFit: 'cover', borderRadius: 8, border: `1px solid ${colors.stone200}` }}
                  />
                </a>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: colors.stone700, fontWeight: 700 }}>Matched transaction proof available</div>
                  {statementProofReference && (
                    <div style={{ fontSize: 12, color: colors.stone500, marginTop: 4, whiteSpace: 'pre-wrap' }}>
                      {statementProofReference}
                    </div>
                  )}
                  <div style={{ marginTop: 6 }}>
                    <a href={paymentProofUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                      Open full proof
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* ─── Actions ─── */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        {isRecurringTemplateMode ? (
          <button className="submit-button" style={s.actionBtn} onClick={() => handleSave('DRAFT')} disabled={saving}>
            <FiCheck size={16} /> Save Recurring Template
          </button>
        ) : (
          <>
            <button className="btn btn-secondary" style={s.actionBtn} onClick={convertToRecurringTemplate} disabled={saving}>
              <FiRepeat size={16} /> Convert to Recurring
            </button>
            <button className="btn btn-secondary" style={s.actionBtn} onClick={() => handleSave('DRAFT')} disabled={saving}>
              Save as Draft
            </button>
            <button className="submit-button" style={s.actionBtn} onClick={() => handleSave('PENDING')} disabled={saving}>
              <FiCheck size={16} /> Submit for Approval
            </button>
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && previewUrl && !isPdf && (
        <div style={s.lightbox} onClick={() => setLightboxOpen(false)}>
          <img src={previewUrl} alt="Receipt full" style={s.lightboxImg} />
        </div>
      )}

      {/* Live camera modal (FaceMatchScreen-style) */}
      {cameraOpen && (
        <div
          style={s.cameraModal}
          onClick={() => { stopLiveCamera(); setCameraOpen(false); }}
        >
          <div style={s.cameraCard} onClick={(e) => e.stopPropagation()}>
            <div style={s.cameraHeader}>
              <h3 style={s.cameraTitle}>Capture receipt</h3>
              <button
                type="button"
                className="btn btn-secondary"
                style={s.smallBtn}
                onClick={() => { stopLiveCamera(); setCameraOpen(false); }}
              >
                Close
              </button>
            </div>
            <div style={s.cameraBody}>
              {cameraError ? (
                <div style={{ fontSize: 13, color: colors.rose600, fontWeight: 600 }}>{cameraError}</div>
              ) : (
                <div style={s.cameraVideoWrap}>
                  <video ref={liveVideoRef} style={s.cameraVideo} playsInline muted />
                </div>
              )}
              <canvas ref={liveCanvasRef} style={{ display: 'none' }} />
            </div>
            <div style={s.cameraFooter}>
              <button
                type="button"
                className="btn btn-secondary"
                style={s.smallBtn}
                onClick={() => { stopLiveCamera(); setCameraOpen(false); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="submit-button"
                style={{ ...s.smallBtn, height: 32, minHeight: 32 }}
                onClick={captureFromLiveCamera}
                disabled={!cameraReady || extracting}
              >
                Capture
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Confirm paid to vendor (non-reimbursement submit) */}
      {paidConfirmOpen && (
        <div style={s.cameraModal} onClick={() => { if (!saving) { setPaidConfirmOpen(false); setPendingSubmitStatus(null); } }}>
          <div style={s.cameraCard} onClick={(e) => e.stopPropagation()}>
            <div style={s.cameraHeader}>
              <h3 style={s.cameraTitle}>Payment details detected</h3>
              <button
                type="button"
                className="btn btn-secondary"
                style={s.smallBtn}
                onClick={() => { if (!saving) { setPaidConfirmOpen(false); setPendingSubmitStatus(null); } }}
              >
                Close
              </button>
            </div>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 13, color: colors.stone700, lineHeight: 1.5 }}>
                You’ve entered payment details. Do you want to <b>mark this as paid to the vendor</b> now?
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: colors.stone500 }}>
                Method: <b>{paymentMethod}</b> • Ref: <b>{paymentReference}</b> • Amount: <b>{fmt(amountPaid)}</b>
              </div>
            </div>
            <div style={s.cameraFooter}>
              <button type="button" className="btn btn-secondary" style={s.smallBtn} onClick={() => confirmPaidAndSave(false)} disabled={saving}>
                No, keep Pending
              </button>
              <button type="button" className="submit-button" style={{ ...s.smallBtn, height: 32, minHeight: 32 }} onClick={() => confirmPaidAndSave(true)} disabled={saving}>
                Yes, mark Paid
              </button>
            </div>
          </div>
        </div>
      )}
      </fieldset>
    </div>
  );
}
